import { Card, Typography } from '@/ui';

import Answer from './Answer';
import AnswerDelta from './AnswerDelta';

import type {
  PulseReportDelta,
  PulseReportDeltaByQuestionKey,
} from './deltaTypes';
import type { PulseReportSection } from '@/types';
import type { FC } from 'react';

interface SectionProps {
  deltaByQuestionKey?: PulseReportDeltaByQuestionKey;
  sectionDelta?: PulseReportDelta;
  section: PulseReportSection;
}

const Section: FC<SectionProps> = ({
  deltaByQuestionKey,
  section,
  sectionDelta,
}) => {
  const answers = [...section.answers].sort((a, b) => a.order - b.order);

  if (!answers.length) {
    return null;
  }

  return (
    <Card
      className="rounded-xl border border-cement-300 bg-white p-6 shadow-none"
      data-testid={`pulse-report-section-${section.key}`}
    >
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
          <Typography className="title-lg text-cement-600" variant="h5">
            {section.title}
          </Typography>
          {section.averageScore !== undefined ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
              <Typography color="text.disabled" variant="body2">
                {section.averageScore.toFixed(1)}/10 average
              </Typography>
              {sectionDelta ? (
                <span data-testid={`pulse-report-section-delta-${section.key}`}>
                  <AnswerDelta
                    className="font-semibold"
                    delta={sectionDelta}
                    variant="body2"
                  />
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        {section.coachNameAtSubmission ? (
          <Typography
            className="ml-auto max-w-[20rem] truncate pt-0.5 text-right text-cement-400"
            data-testid={`pulse-report-section-context-${section.key}`}
            variant="body2"
          >
            {section.coachNameAtSubmission}
          </Typography>
        ) : null}
      </div>
      <div className="mt-4 flex flex-col gap-6">
        {answers.map((answer) => (
          <Answer
            key={answer.id}
            answer={answer}
            deltaByQuestionKey={deltaByQuestionKey}
          />
        ))}
      </div>
    </Card>
  );
};

export default Section;
